import type { SigninPopupArgs, SigninRedirectArgs } from "oidc-client-ts";
import React from "react";
import {
  type AuthContextProps,
  type AuthState,
  hasAuthParams,
  useAuth,
} from "react-oidc-context";

type UseAutoSignInProps = {
  signinMethod?: keyof Pick<AuthContextProps, "signinRedirect" | "signinPopup">;
  args?: SigninRedirectArgs | SigninPopupArgs;
};

type UseAutoSignInReturn = Pick<
  AuthState,
  "isAuthenticated" | "isLoading" | "error"
>;

/**
 * Adapted from https://github.com/authts/react-oidc-context/blob/main/src/useAutoSignin.ts
 */
export const useAutoSignin = ({
  signinMethod = "signinRedirect",
  args,
}: UseAutoSignInProps = {}): UseAutoSignInReturn => {
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = React.useState(false);

  const shouldAttemptSignin = React.useMemo(
    () =>
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !hasTriedSignin,
    [
      auth.activeNavigator,
      auth.isAuthenticated,
      auth.isLoading,
      hasTriedSignin,
    ],
  );

  React.useEffect(() => {
    if (shouldAttemptSignin) {
      switch (signinMethod) {
        case "signinPopup":
          void auth.signinPopup(args);
          break;
        case "signinRedirect":
        default:
          void auth.signinRedirect(args);
          break;
      }

      setHasTriedSignin(true);
    }
  }, [auth, hasTriedSignin, shouldAttemptSignin, signinMethod, args]);

  return {
    isLoading: auth.isLoading,
    isAuthenticated: auth.isAuthenticated,
    error: auth.error,
  };
};
